import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { useGetCelebrations } from '../../queries/marketing';

const THEME_EMOJI = {
  party_neon: '🎉', royal_gold: '👑', wow_pop: '🌟', royal_dark: '🌙', sweet_candy: '🍭',
};

const NOTIFY_SEVERITY = {
  pending: 'secondary', sent: 'success', apologized: 'warning', failed: 'danger', quota_blocked: 'danger',
};

const RECAP_SEVERITIES = { ready: 'success', rendering: 'info', queued: 'secondary', failed: 'danger' };

function RecapChip({ recap, t }) {
  if (!recap) return <span className="text-400">—</span>;
  const sev = RECAP_SEVERITIES[recap.status] ?? 'secondary';
  if (recap.status === 'ready') {
    return <Tag severity={sev} value={t('marketing.act_cel_recap_ready', { dur: recap.duration_s ?? '?' })} />;
  }
  return <Tag severity={sev} value={t(`marketing.act_cel_recap_${recap.status}`, recap.status)} />;
}

function RecipientTable({ recipients, t }) {
  if (!recipients || recipients.length === 0) {
    return <div className="text-400 text-sm">{t('marketing.act_cel_no_guests')}</div>;
  }
  const rows = recipients.map((r, i) => ({ ...r, _key: i }));
  return (
    <DataTable value={rows} dataKey="_key" size="small" responsiveLayout="scroll">
      <Column
        header={t('marketing.act_cel_guest_name')}
        body={(r) => (
          <span>
            {r.name || <span className="text-400">—</span>}
            {r.is_birthday_person && (
              <Tag className="ml-2" severity="warning" value="★" style={{ fontSize: '0.65rem' }} />
            )}
          </span>
        )}
      />
      <Column
        header={t('marketing.act_cel_guest_notify')}
        body={(r) => (
          <Tag
            severity={NOTIFY_SEVERITY[r.notify_status] ?? 'secondary'}
            value={t(`marketing.notify_${r.notify_status}`, r.notify_status)}
          />
        )}
      />
      <Column
        header={t('marketing.act_cel_guest_delivered')}
        body={(r) => (r.delivered ? <span className="text-green-500 font-bold">✓</span> : <span className="text-400">—</span>)}
      />
      <Column
        header={t('marketing.act_cel_guest_opened')}
        body={(r) => (r.opened ? <span className="text-green-500 font-bold">✓</span> : <span className="text-400">—</span>)}
      />
      <Column
        header={t('marketing.act_cel_guest_clicked')}
        body={(r) => (r.clicked ? <span className="text-green-500 font-bold">✓</span> : <span className="text-400">—</span>)}
      />
    </DataTable>
  );
}

function rowExpansionTemplate(row, t) {
  return (
    <div className="p-3 surface-ground border-round">
      {/* Photos */}
      <div className="mb-3">
        <span className="font-semibold mr-2">{t('marketing.act_cel_photos')}:</span>
        <Tag className="mr-1" severity="success" value={`${row.media.approved} ${t('marketing.act_cel_media_approved')}`} />
        {row.media.rejected > 0 && (
          <Tag className="mr-1" severity="danger" value={`${row.media.rejected} ${t('marketing.act_cel_media_rejected')}`} />
        )}
        {row.media.flagged > 0 && (
          <Tag className="mr-1" severity="warning" value={`${row.media.flagged} ${t('marketing.act_cel_media_flagged')}`} />
        )}
        {row.media.total === 0 && <span className="text-400">—</span>}
      </div>

      {/* Recap */}
      <div className="mb-3">
        <span className="font-semibold mr-2">{t('marketing.act_cel_recap')}:</span>
        <RecapChip recap={row.recap} t={t} />
      </div>

      {/* Guests */}
      <div className="mb-3">
        <div className="font-semibold mb-2">{t('marketing.act_cel_guests')}:</div>
        <RecipientTable recipients={row.recipients} t={t} />
      </div>

      {/* CRM */}
      <div className="text-sm text-600">
        {t('marketing.act_cel_crm_linked', { count: row.crm_records_linked })}
      </div>
    </div>
  );
}

export default function CelebrationsPanel({ period, from, to }) {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState(null);

  const { data, isLoading } = useGetCelebrations(
    from && to ? { period, from, to } : { period }
  );

  const summary = data?.summary ?? {};
  const sessions = data?.sessions ?? [];

  const kpiCards = [
    { key: 'total_celebrations', tKey: 'act_cel_kpi_total',   color: 'var(--purple-500)' },
    { key: 'photos_approved',    tKey: 'act_cel_kpi_photos',   color: 'var(--green-500)' },
    { key: 'opt_ins',            tKey: 'act_cel_kpi_opt_ins',  color: 'var(--blue-500)' },
    { key: 'new_crm_records',    tKey: 'act_cel_kpi_crm',      color: 'var(--teal-500)' },
  ];

  return (
    <div className="mb-5">
      <h3 className="mt-0 mb-3">{t('marketing.act_celebrations_title')}</h3>

      {/* KPI cards */}
      <div className="grid mb-3">
        {kpiCards.map((c) => (
          <div key={c.key} className="col-6 md:col-3">
            <div className="surface-card border-round p-3 text-center">
              <div className="text-2xl font-bold" style={{ color: c.color }}>
                {summary[c.key] ?? 0}
              </div>
              <div className="text-500 text-sm">{t(`marketing.${c.tKey}`)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Sessions table */}
      <DataTable
        value={sessions}
        loading={isLoading}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={(row) => rowExpansionTemplate(row, t)}
        dataKey="id"
        emptyMessage={t('marketing.act_celebrations_empty')}
        responsiveLayout="scroll"
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25]}
      >
        <Column expander style={{ width: '3rem' }} />
        <Column
          header={t('marketing.act_cel_person')}
          body={(r) => r.birthday_person_name || <span className="text-400">—</span>}
        />
        <Column
          header={t('marketing.act_cel_theme')}
          body={(r) => (
            <span>{THEME_EMOJI[r.theme] ?? '🎂'} {t(`marketing.theme_${r.theme}`, r.theme)}</span>
          )}
        />
        <Column
          header={t('marketing.act_cel_date')}
          body={(r) => (r.completed_at ? new Date(r.completed_at).toLocaleDateString() : '—')}
        />
        <Column
          header={t('marketing.act_cel_photos')}
          body={(r) => `${r.media.approved} / ${r.media.total}`}
        />
        <Column
          header={t('marketing.act_cel_recap')}
          body={(r) => <RecapChip recap={r.recap} t={t} />}
        />
        <Column
          header={t('marketing.act_cel_guests')}
          body={(r) => r.recipients.length}
        />
      </DataTable>
    </div>
  );
}
